import React from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

interface DepartmentMentorPerformanceChartProps {
  data: Array<{
    department: string;
    mentor_count: number;
    success_rate: number;
    avg_workshop_duration: number;
    mentee_satisfaction: number;
    workshop_count: number;
  }>;
  loading?: boolean;
}

const DepartmentMentorPerformanceChart: React.FC<DepartmentMentorPerformanceChartProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Mentor Performance</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Mentor Performance</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No mentor performance data available
        </div>
      </div>
    );
  }

  const colors = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', 
    '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1'
  ];

  const getPerformanceLevel = (successRate: number, satisfaction: number) => {
    const score = (successRate + satisfaction) / 2;
    if (score >= 80) return { level: 'Excellent', color: '#10b981' };
    if (score >= 60) return { level: 'Good', color: '#3b82f6' };
    if (score >= 40) return { level: 'Average', color: '#f59e0b' };
    return { level: 'Needs Improvement', color: '#ef4444' };
  };

  const getDepartmentColor = (index: number) => colors[index % colors.length];

  const formatDuration = (days: number) => {
    if (days >= 365) return `${(days / 365).toFixed(1)}y`;
    if (days >= 30) return `${(days / 30).toFixed(1)}m`;
    return `${days.toFixed(0)}d`;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Mentor Performance</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              type="number" 
              dataKey="mentor_count" 
              name="Mentor Count"
              tick={{ fontSize: 12 }}
              domain={[0, 'dataMax + 1']}
            />
            <YAxis 
              type="number" 
              dataKey="success_rate" 
              name="Success Rate (%)"
              tick={{ fontSize: 12 }}
              domain={[0, 100]}
            />
            <Tooltip 
              formatter={(value: number, name: string, props: any) => {
                if (name === 'mentor_count') return [`${value} mentors`, 'Mentor Count'];
                if (name === 'success_rate') return [`${value.toFixed(1)}%`, 'Success Rate'];
                if (name === 'avg_workshop_duration') return [formatDuration(value), 'Avg Duration'];
                if (name === 'mentee_satisfaction') return [`${value.toFixed(1)}%`, 'Satisfaction'];
                if (name === 'workshop_count') return [`${value} workshops`, 'Workshop Count'];
                return [value, name];
              }}
              labelFormatter={(label, payload) => {
                if (payload && payload[0]) {
                  return `Department: ${payload[0].payload.department}`;
                }
                return '';
              }}
            />
            <Scatter dataKey="success_rate" fill="#8884d8">
              {data.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={getDepartmentColor(index)} 
                />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Mentor Performance</h4>
          <div className="space-y-2">
            {[...data]
              .sort((a, b) => (b.success_rate + b.mentee_satisfaction) - (a.success_rate + a.mentee_satisfaction))
              .map((item, index) => {
                const performance = getPerformanceLevel(item.success_rate, item.mentee_satisfaction);
                return (
                  <div key={item.department} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded mr-2" 
                        style={{ backgroundColor: getDepartmentColor(index) }}
                      ></div>
                      <span className="text-sm font-medium">{item.department}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">{item.mentor_count}</span> mentors
                      <span className={`ml-2 text-xs px-2 py-1 rounded`} style={{ 
                        backgroundColor: performance.color + '20', 
                        color: performance.color 
                      }}>
                        {performance.level}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Mentor Insights</h4>
          <div className="space-y-3">
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-green-600">Best Performance</span>
                <span className="font-bold text-green-600">
                  {data.reduce((max, item) => 
                    (item.success_rate + item.mentee_satisfaction) > (max.success_rate + max.mentee_satisfaction) ? item : max, 
                    data[0]
                  )?.department}
                </span>
              </div>
              <div className="text-xs text-green-500 mt-1">
                {Math.max(...data.map(d => d.success_rate)).toFixed(1)}% success rate
              </div>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-blue-600">Most Mentors</span>
                <span className="font-bold text-blue-600">
                  {data.reduce((max, item) => item.mentor_count > max.mentor_count ? item : max, data[0])?.department}
                </span>
              </div>
              <div className="text-xs text-blue-500 mt-1">
                {Math.max(...data.map(d => d.mentor_count))} mentors
              </div>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-purple-600">Highest Satisfaction</span>
                <span className="font-bold text-purple-600">
                  {data.reduce((max, item) => item.mentee_satisfaction > max.mentee_satisfaction ? item : max, data[0])?.department}
                </span>
              </div>
              <div className="text-xs text-purple-500 mt-1">
                {Math.max(...data.map(d => d.mentee_satisfaction)).toFixed(1)}% satisfaction
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepartmentMentorPerformanceChart;
